import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { setSelectedSection } from '../../reduxStore/actions/questionActionsHelper';
import { getUserSectionStats } from '../../reduxStore/actions/recordActionsHelper';
import { userSectionStats$ } from '../../reduxStore/selectors/recordSelectors';
import { usersData$ } from '../../reduxStore/selectors/userSelectors';
import sectionType from '../../utils/Enum/sectionType';
import AccuracyProgressBar from '../AccuracyProgressBar';
import SubscriptionOverlay from '../subscriptionOverlay/subscriptionOverlay';
import styles from './asset/SATReadingAndWritingSelectorBox.module.scss';

const SATReadingAndWritingSelectorBox = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUser = useSelector(usersData$);
    const userSectionStats = useSelector(userSectionStats$);

    // Check subscription status
    const hasSubscription = currentUser?.subscription_end_date !== null &&
        currentUser?.subscription_end_date !== undefined;

    // Fetch user statistics when component mounts
    useEffect(() => {
        if (currentUser?.id) {
            dispatch(getUserSectionStats.request({ userId: currentUser.id }));
        }
    }, [dispatch, currentUser?.id]);

    // Get accuracy color based on percentage
    const getAccuracyColor = (accuracy) => {
        if (accuracy === 0) return '#9ca3af'; // Grey for no practice (0%)
        if (accuracy >= 80) return '#22c55e'; // Green for excellent (80-100%)
        if (accuracy >= 60) return '#eab308'; // Yellow for good (60-79%)
        return '#ef4444'; // Red for needs improvement (1-59%)
    };

    // Get section statistics
    const getSectionStats = (sectionName) => {
        if (!userSectionStats || !userSectionStats[sectionName]) {
            return { accuracy: 0, lastPracticeDate: '---', color: getAccuracyColor(0) };
        }

        const accuracy = userSectionStats[sectionName].accuracy || 0;
        const lastPracticeDate = userSectionStats[sectionName].lastPracticeDate || '---';

        return {
            accuracy,
            lastPracticeDate,
            color: getAccuracyColor(accuracy)
        };
    };

    // Calculate overall reading and writing accuracy
    const getReadingWritingStats = () => {
        if (!userSectionStats) {
            return { accuracy: 0, date: '---', color: getAccuracyColor(0) };
        }

        const infoIdeas = userSectionStats[sectionType.INFORMATION_AND_IDEAS]?.accuracy || 0;
        const craftStructure = userSectionStats[sectionType.CRAFT_AND_STRUCTURE]?.accuracy || 0;
        const expressionIdeas = userSectionStats[sectionType.EXPRESSION_OF_IDEAS]?.accuracy || 0;
        const englishConventions = userSectionStats[sectionType.STANDARD_ENGLISH_CONVENTIONS]?.accuracy || 0;

        // Average accuracy across all reading and writing sections
        const totalSections = [infoIdeas, craftStructure, expressionIdeas, englishConventions].filter(acc => acc > 0);
        const accuracy = totalSections.length > 0
            ? Math.round(totalSections.reduce((sum, acc) => sum + acc, 0) / totalSections.length)
            : 0;

        // Get most recent practice date from all sections
        const dates = [
            userSectionStats[sectionType.INFORMATION_AND_IDEAS]?.lastPracticeDate,
            userSectionStats[sectionType.CRAFT_AND_STRUCTURE]?.lastPracticeDate,
            userSectionStats[sectionType.EXPRESSION_OF_IDEAS]?.lastPracticeDate,
            userSectionStats[sectionType.STANDARD_ENGLISH_CONVENTIONS]?.lastPracticeDate
        ].filter(date => date && date !== '---');

        let latestDate = '---';
        if (dates.length > 0) {
            latestDate = dates.reduce((latest, current) => {
                return new Date(current) > new Date(latest) ? current : latest;
            });
        }

        return { accuracy, date: latestDate, color: getAccuracyColor(accuracy) };
    };

    const handlePracticeClick = (section) => {
        dispatch(setSelectedSection.request(section));
        navigate('/user-practice-session');
    };

    const handlePracticeAllReadingWriting = () => {
        // Set practice all reading/writing section for unified handling
        dispatch(setSelectedSection.request(sectionType.PRACTICE_ALL_READING_WRITING));
        navigate('/user-practice-session');
    };

    const overallStats = getReadingWritingStats();
    return (
        <div style={{ position: 'relative' }}>
            <div className={styles.tableSatPractice}>
                <div className={styles.satReadingAndWrittngPractiParent}>
                    <div className={styles.satReadingAnd}>Practice Reading & Writing Modules</div>
                    <div className={styles.frameParent}>
                        <div className={styles.frameGroup}>
                            <div className={styles.frameGroup}>
                                <svg className={styles.warningCircleWarning} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 8v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.accuracy}>Mastery</div>
                            </div>
                            <div className={styles.wrapper}>
                                <div className={styles.accuracy} style={{ color: overallStats.color }}>{overallStats.accuracy}%</div>
                            </div>
                        </div>
                        <div className={styles.calendarCalendarCheckParent}>
                            <svg className={styles.warningCircleWarning} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 14l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className={styles.accuracy}>Date</div>
                        </div>
                        <div className={styles.buttonPractice} onClick={() => handlePracticeAllReadingWriting()}>
                            <div className={styles.accuracy}>Module Practice</div>
                        </div>
                    </div>
                </div>
                <div className={styles.tableSatPracticeChild} />
                <div className={styles.frameContainer}>
                    <div className={styles.frameDiv}>
                        <div className={styles.tagSatPracticeParent}>
                            <div className={styles.tagSatPractice}>
                                <svg className={styles.tagSatPracticeChild} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.informationAndIdeasParent}>
                                    <div className={styles.informationAndIdeas}>{sectionType.INFORMATION_AND_IDEAS}</div>
                                    <div className={styles.explanationSentence}>Read, interpret, and reason through texts and data graphics.</div>
                                </div>
                            </div>
                            <div className={styles.accuracyBar}>
                                <AccuracyProgressBar
                                    accuracy={getSectionStats(sectionType.INFORMATION_AND_IDEAS).accuracy}
                                    width="120px"
                                    height="8px"
                                />
                            </div>
                        </div>
                        <div className={styles.april142025}>{getSectionStats(sectionType.INFORMATION_AND_IDEAS).lastPracticeDate}</div>
                    </div>
                    <button className={styles.buttonMorePractice} onClick={() => handlePracticeClick(sectionType.INFORMATION_AND_IDEAS)}>
                        <div className={styles.accuracy}>Practice</div>
                    </button>
                </div>
                <div className={styles.tableSatPracticeChild} />
                <div className={styles.frameContainer}>
                    <div className={styles.frameDiv}>
                        <div className={styles.tagSatPracticeParent}>
                            <div className={styles.tagSatPractice}>
                                <svg className={styles.tagSatPracticeChild} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.informationAndIdeasParent}>
                                    <div className={styles.informationAndIdeas}>{sectionType.CRAFT_AND_STRUCTURE}</div>
                                    <div className={styles.explanationSentence}>Master vocabulary in context and analyze how texts are built.</div>
                                </div>
                            </div>
                            <div className={styles.accuracyBar}>
                                <AccuracyProgressBar
                                    accuracy={getSectionStats(sectionType.CRAFT_AND_STRUCTURE).accuracy}
                                    width="120px"
                                    height="8px"
                                />
                            </div>
                        </div>
                        <div className={styles.april142025}>{getSectionStats(sectionType.CRAFT_AND_STRUCTURE).lastPracticeDate}</div>
                    </div>
                    <button className={styles.buttonMorePractice} onClick={() => handlePracticeClick(sectionType.CRAFT_AND_STRUCTURE)}>
                        <div className={styles.accuracy}>Practice</div>
                    </button>
                </div>
                <div className={styles.tableSatPracticeChild} />
                <div className={styles.frameContainer}>
                    <div className={styles.frameDiv}>
                        <div className={styles.tagSatPracticeParent}>
                            <div className={styles.tagSatPractice}>
                                <svg className={styles.tagSatPracticeChild} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.informationAndIdeasParent}>
                                    <div className={styles.informationAndIdeas}>{sectionType.EXPRESSION_OF_IDEAS}</div>
                                    <div className={styles.explanationSentence}>Revise writing to make it clearer and more effective.</div>
                                </div>
                            </div>
                            <div className={styles.accuracyBar}>
                                <AccuracyProgressBar
                                    accuracy={getSectionStats(sectionType.EXPRESSION_OF_IDEAS).accuracy}
                                    width="120px"
                                    height="8px"
                                />
                            </div>
                        </div>
                        <div className={styles.april142025}>{getSectionStats(sectionType.EXPRESSION_OF_IDEAS).lastPracticeDate}</div>
                    </div>
                    <button className={styles.buttonMorePractice} onClick={() => handlePracticeClick(sectionType.EXPRESSION_OF_IDEAS)}>
                        <div className={styles.accuracy}>Practice</div>
                    </button>
                </div>
                <div className={styles.lineDiv} />
                <div className={styles.frameContainer}>
                    <div className={styles.frameDiv}>
                        <div className={styles.tagSatPracticeParent}>
                            <div className={styles.tagSatPractice}>
                                <svg className={styles.tagSatPracticeChild} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#1e0ea4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className={styles.informationAndIdeasParent}>
                                    <div className={styles.informationAndIdeas}>{sectionType.STANDARD_ENGLISH_CONVENTIONS}</div>
                                    <div className={styles.explanationSentence}>Fix grammar, sentence structure, and punctuation errors.</div>
                                </div>
                            </div>
                            <div className={styles.accuracyBar}>
                                <AccuracyProgressBar
                                    accuracy={getSectionStats(sectionType.STANDARD_ENGLISH_CONVENTIONS).accuracy}
                                    width="120px"
                                    height="8px"
                                />
                            </div>
                        </div>
                        <div className={styles.april142025}>{getSectionStats(sectionType.STANDARD_ENGLISH_CONVENTIONS).lastPracticeDate}</div>
                    </div>
                    <button className={styles.buttonMorePractice} onClick={() => handlePracticeClick(sectionType.STANDARD_ENGLISH_CONVENTIONS)}>
                        <div className={styles.accuracy}>Practice</div>
                    </button>
                </div>
            </div>
            <SubscriptionOverlay isActive={!hasSubscription} positionType="absolute" />
        </div>
    );
};

export default SATReadingAndWritingSelectorBox;
